import { Link } from 'react-router-dom';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api';
import { useEffect, useState } from 'react';
import {
  LayoutGrid, Tag, Sparkles, HelpCircle, MessageSquareWarning, Store, Truck, ShieldCheck, ChevronDown,
} from 'lucide-react';

/**
 * Secondary navigation bar on the landing / catalog. Sits between the main
 * header and the hero. Renders as horizontal tabs on desktop and collapses
 * gracefully on mobile via horizontal scroll (no hamburger, keeping the shortcut
 * concept explicit).
 */
export const ShortcutBar = () => {
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.get('/categories').then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, []);

  return (
    <nav
      data-testid="shortcut-bar"
      className="border-b border-slate-200 bg-slate-50/80"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center gap-1 overflow-x-auto whitespace-nowrap h-11">
        {/* Categorías dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="shortcut-categorias"
              className="inline-flex items-center gap-1.5 px-3 h-full text-sm font-semibold text-slate-700 hover:text-slate-900 hover:bg-white transition-colors"
            >
              <LayoutGrid className="h-4 w-4" /> Categorías <ChevronDown className="h-3 w-3 opacity-60" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="rounded-none w-56">
            {categories.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-500">Cargando categorías…</div>
            ) : categories.map((c) => (
              <DropdownMenuItem key={c} asChild>
                <Link
                  to={`/catalogo?category=${encodeURIComponent(c)}`}
                  data-testid={`shortcut-cat-${c.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {c}
                </Link>
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem asChild>
              <Link to="/catalogo" className="text-blue-700 font-semibold">Ver catálogo completo →</Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <ShortcutLink to="/ofertas" icon={Tag} label="Ofertas" testId="shortcut-ofertas" />
        <ShortcutLink to="/novedades" icon={Sparkles} label="Novedades" testId="shortcut-novedades" />
        <ShortcutLink to="/registro?rol=seller" icon={Store} label="Vender en AutoMarket" testId="shortcut-vender" hideOnMobile />
        <ShortcutLink to="/envios" icon={Truck} label="Envíos" testId="shortcut-envios" hideOnMobile />
        <ShortcutLink to="/garantias" icon={ShieldCheck} label="Garantías" testId="shortcut-garantias" hideOnMobile />

        <div className="flex-1" />

        <ShortcutLink to="/ayuda" icon={HelpCircle} label="Ayuda" testId="shortcut-ayuda" />
        <ShortcutLink to="/pqr" icon={MessageSquareWarning} label="PQR" testId="shortcut-pqr" />
      </div>
    </nav>
  );
};

const ShortcutLink = ({ to, icon: Icon, label, testId, hideOnMobile }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`${hideOnMobile ? 'hidden md:inline-flex' : 'inline-flex'} items-center gap-1.5 px-3 h-full text-sm font-semibold text-slate-700 hover:text-slate-900 hover:bg-white transition-colors`}
  >
    <Icon className="h-4 w-4" /> {label}
  </Link>
);
